import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@/types';
import { sessionStorageUtils } from '@/lib/storageUtils';
import { emailApiService } from '../services/emailApiService';
import type { EmailLinkValidationStatus } from '../types/email';

const TOKEN_QUERY_PARAM = 'token';
const VALIDATED_TOKEN_STORAGE_KEY = 'email-link-validated-token';

const statusByError: Record<string, EmailLinkValidationStatus> = {
  invalid_code: 'invalidLink',
  limit_exceeded: 'limitExceeded',
  unexpected: 'notValidatable',
};

interface UseEmailLinkValidationReturn {
  status: EmailLinkValidationStatus;
  validate: () => Promise<void>;
}

/**
 * Drives the email validation link landing page, which validates on arrival
 * without waiting for a click.
 *
 * The token is single use, so it must be spent exactly once per page load:
 * `isTokenSpentRef` is what keeps the StrictMode double-effect (and a reload
 * after a success) from turning a validated email into an `invalid_code`
 * screen. Only a request that failed to reach the API is retried, since that
 * one left the token unspent.
 */
export const useEmailLinkValidation = (): UseEmailLinkValidationReturn => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(TOKEN_QUERY_PARAM)?.trim() ?? '';

  const [status, setStatus] = useState<EmailLinkValidationStatus>(() => {
    if (!token) {
      return 'invalidLink';
    }

    // A reload after a successful validation must not re-POST a spent token
    // and turn the success screen into an error.
    const validatedToken = sessionStorageUtils.get<string>(
      VALIDATED_TOKEN_STORAGE_KEY
    );

    return validatedToken === token ? 'success' : 'idle';
  });

  const isTokenSpentRef = useRef(status === 'success');

  const { mutateAsync: validateEmailByLink } = useMutation({
    mutationFn: (linkToken: string) =>
      emailApiService.validateEmailByLink(linkToken),
  });

  const validate = useCallback(async () => {
    if (!token || isTokenSpentRef.current) {
      return;
    }

    isTokenSpentRef.current = true;
    setStatus('pending');

    try {
      const result = await validateEmailByLink(token);

      // `already_validated` means this same link already confirmed the address:
      // a mail scanner can spend the token before the recipient's browser gets
      // to it, so for the user this is a success, not an error.
      if (result.isSuccess || result.error === 'already_validated') {
        sessionStorageUtils.set(VALIDATED_TOKEN_STORAGE_KEY, token);
        setStatus('success');
        return;
      }

      setStatus(statusByError[result.error ?? ''] ?? 'notValidatable');
    } catch (error) {
      // The API rejected the token itself - retrying cannot change the outcome.
      if (error instanceof ApiError && error.status === 400) {
        setStatus('invalidLink');
        return;
      }

      isTokenSpentRef.current = false;
      setStatus('failed');
    }
  }, [token, validateEmailByLink]);

  return { status, validate };
};
